/**
 * The notes one part sounds, over the whole played order.
 *
 * This is the half of the schedule that reads the music; `schedule.ts` is the half that
 * lays out the bars it is read into. Everything a written note is not -- a tie that joins
 * two of them, a grace that steals time from the next, a mark asking for notes nobody wrote
 * -- is resolved here and in `schedule-heads.ts`, which turns each note reached into sound,
 * so that what comes out is a flat list of sounds with times on them.
 *
 * How loud a note is cannot be known as the note is reached, because a hairpin grows
 * towards a mark written after it. So the marks are collected into a curve as the part is
 * read, and the velocities are filled in once at the end, from where each note stands.
 */

import type { EventId } from "../document/ids";
import { partKey } from "../document/key";
import { capacityOf, instrumentAt, resolveAttributes } from "../document/query";
import { lineNames } from "../document/standing";
import { inTime, tieLedger } from "../document/ties";
import type { DynamicMark, Part, Score } from "../document/types";
import { VELOCITIES, curveOf, loudnessOf, reading } from "./dynamics";
import type { Level, Ramp, Strike } from "./dynamics";
import { onsets } from "./graces";
import { hairpinsOf } from "./hairpins";
import { joined as legato, slursOf } from "./legato";
import { heldFor, shapeOf } from "./realize";
import { soundHeads } from "./schedule-heads";
import type { Reached, Sounded } from "./schedule-heads";
import type { BarTime, Hold, PartPlay, ScheduleOptions } from "./played";
import { bridgingOf, mutes, mutingOf } from "./manner";
import { bowed, pluckingOf } from "./plucking";

/** A note with nothing written on it: full length, and as loud as the passage. */
const PLAIN = { length: 1, velocity: 1 };

/** The signature this part prints at a bar, which is the scale its ornaments move in. */
function fifthsOf(score: Score, part: Part, index: number, cache: Map<number, number>): number {
  const known = cache.get(index);
  if (known !== undefined) return known;
  // Against the instrument in the player's hands at that bar, not the one the part opened
  // on: a flute that has taken up a clarinet reads a signature a tone away from its own.
  const key = resolveAttributes(score, index).key;
  const fifths = partKey(key, instrumentAt(score, part, index)).fifths;
  cache.set(index, fifths);
  return fifths;
}

export function partNotes(
  score: Score,
  part: Part,
  times: readonly BarTime[],
  options: ScheduleOptions,
): PartPlay {
  const marks = options.marks ?? true;
  const hairpins = hairpinsOf(score, part);
  const slurs = slursOf(score, part);
  const plucking = pluckingOf(score, part);
  const muting = mutingOf(score, part);
  const bridging = bridgingOf(score, part);
  const lines = lineNames(score, part);
  const signatures = new Map<number, number>();

  const holds: Hold[] = [];
  const levels: Level[] = [];
  const ramps: Ramp[] = [];
  const strikes: Strike[] = [];
  const weights = new Map<EventId, number>();
  // Ties are carried in one ledger that lives across bars and is kept for the whole part in
  // the order played: a tie is as likely to join two notes inside one bar as to cross a bar
  // line, and the head that ends it is found by its pitch, not by the voice it is kept in.
  const sounded: Sounded = {
    notes: [],
    lines: [],
    struck: [],
    carried: new Set<number>(),
    ties: tieLedger<number>(),
  };

  for (const bar of times) {
    const measureId = score.measures[bar.index]?.id;
    const measure = measureId === undefined ? undefined : part.measures[measureId];
    if (measure === undefined) continue;
    // What the notes sound moves with the instrument, as the signature does: they are what
    // the player reads, and reading them on another instrument sounds somewhere else.
    const playing = instrumentAt(score, part, bar.index);
    const fifths = fifthsOf(score, part, bar.index, signatures);
    // A word that asks for plucking asks it of a bow. It is one of the marks, read as an
    // articulation is: what the player does with the notes, and not which notes they are.
    const plucks = marks && bowed(playing.midiProgram);
    // So is a mute, of a bow or of brass, and the bridge, of a bow alone.
    const muted = marks && mutes(playing.midiProgram);
    const barEnd = bar.ticks + capacityOf(score, bar.index);

    const dynamics = new Map<EventId, DynamicMark>();
    for (const direction of measure.directions) {
      if (direction.content.kind === "dynamic")
        dynamics.set(direction.anchor, direction.content.mark);
    }

    // An ossia is what the player may do instead, not what the part plays: it is read off
    // the page and chosen, and sounding it alongside the line it replaces would play the bar
    // twice at once. A line goes by the name it keeps from bar to bar, not by the index this
    // bar keeps it at: a slur and the legato under it run on over the bar line in the line
    // they began in.
    const voices = measure.voices.flatMap((voice, voiceIndex) => {
      if (voice.ossia) return [];
      const line = lines.get(measureId)?.[voiceIndex] ?? `${voiceIndex}`;
      return [{ voice, line, placed: onsets(voice) }];
    });
    // The slurs are read a bar at a time, one of them ending where a note of another voice
    // than its own begins. A slur the played order has left is over, and the note of its line
    // sounding up to this bar line was the last played under it, which a slur does not carry
    // on from. A hairpin it has left is over at this bar line too.
    if (marks) ramps.push(...hairpins.bar(bar.index, bar.ticks));
    for (const line of marks ? slurs.bar(bar.index, voices) : []) {
      for (const at of sounded.carried) {
        const note = sounded.notes[at];
        if (note === undefined || sounded.lines[at] !== line) continue;
        if (note.startTicks + note.durationTicks >= bar.ticks) sounded.carried.delete(at);
      }
    }

    const reached: Reached[] = [];
    for (const { voice, line, placed } of voices) {
      placed.forEach(({ event, start, ticks }, order) => {
        const atTicks = bar.ticks + start;

        const mark = dynamics.get(event.id);
        if (mark !== undefined) {
          const { level, strike } = reading(mark, atTicks);
          if (level !== null) levels.push(level);
          if (strike !== null) strikes.push(strike);
        }
        if (marks) {
          ramps.push(...hairpins.event(event.id, atTicks, ticks));
          if (event.fermata !== null) {
            const extra = heldFor(event.fermata, ticks);
            holds.push({ at: atTicks, ticks: extra, seconds: extra * bar.rate });
          }
        }

        if (event.kind !== "note") return;
        // A cue is another instrument's music, printed here only so the player can hear
        // their entry coming in the room rather than from their own stand. Sounding it
        // would double the part it was quoted from.
        if (event.cue) return;

        const shape = marks ? shapeOf(event.articulations) : PLAIN;
        if (shape.velocity !== 1) weights.set(event.id, shape.velocity);
        reached.push({
          event,
          line,
          staff: event.staff ?? voice.staff,
          start,
          ticks,
          // Where the next note of the line begins, and never past the bar line: a written
          // note ends there however much an overfull bar holds, and the tie out of it is
          // taken up by the bar that follows.
          ends: Math.min(bar.ticks + (placed[order + 1]?.start ?? start + ticks), barEnd),
          shape,
          // A slur carries on from every note under it but its last, and a shortened note
          // under a slur is a portato, played apart, not joined.
          carries: shape.length === 1 && slurs.carrying(line, order, start),
          plucked: plucks && plucking.at(bar.index, start),
          muted: muted && muting.at(bar.index, start),
          bridge: plucks && bridging.at(bar.index, start),
        });
      });
    }

    // The marks are read a voice at a time and the notes are sounded as they come, across
    // the voices, so that a tie handed from one voice to another finds the note it began on
    // already sounding whichever of the two the bar keeps first.
    for (const note of inTime(reached)) {
      soundHeads(sounded, note, { part, bar, playing, fifths, marks });
    }
  }

  // The end of the piece is a bar no hairpin covers.
  const last = times[times.length - 1];
  if (marks && last !== undefined) {
    const end = last.ticks + capacityOf(score, last.index);
    ramps.push(...hairpins.bar(score.measures.length, end));
  }

  const curve = curveOf(levels, ramps, strikes, options.velocity ?? VELOCITIES.mf);
  // Joined first, so that a note swells over the whole of the time it sounds.
  const { notes: held, overlaps } = legato(sounded.notes, sounded.lines, sounded.carried);
  return {
    notes: held.map((note, index) => ({
      ...note,
      ...loudnessOf(
        curve,
        note.startTicks,
        note.durationTicks,
        weights.get(note.eventId) ?? 1,
        sounded.struck[index] ?? note.startTicks,
      ),
    })),
    overlaps,
    holds,
  };
}
